/* Recover unavailable portraits; ready carts proceed directly to Shopify checkout. */
(() => {
  if (window.PopPrintsPersonalizedCart) return;
  const KEY = 'popPrints.cartRecovery.v1';
  const MARKER = '_PP Recovery Attempt';
  let task = null;
  let scheduled;
  let bypass = false;
  let checkoutPending = false;
  let savedOperation = null;
  const attempted = new Set();
  const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const id = (value) => String(value || '').replace(/^gid:\/\/shopify\/(Product|ProductVariant)\//, '');
  const route = (path) => new URL(path.startsWith('/') ? path :
    (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/') + path, location.origin).href;
  const managed = (line) => Boolean(line.properties?.['_PP Design ID'] && line.properties?.['_PP Source Variant ID']);
  const country = () => String(window.Shopify?.country ||
    document.querySelector('[data-pp-cart-country]')?.dataset.ppCartCountry || 'US').trim().toUpperCase();
  const recoveryKeys = (countryCode = country()) => ['US', 'CA', 'GB'].includes(countryCode)
    ? [...new Set([...document.querySelectorAll('[data-pp-recovery-key]')]
      .map((element) => element.dataset.ppRecoveryKey).filter(Boolean))]
      .filter((key) => !attempted.has(countryCode + ':' + key)) : [];
  const clearStatus = () => {
    for (const element of document.querySelectorAll('[data-pp-cart-recovery-status]')) element.remove();
  };
  const showStatus = (error) => {
    let element = document.querySelector('[data-pp-cart-recovery-status]');
    if (!element) {
      element = document.createElement('div');
      element.dataset.ppCartRecoveryStatus = 'true';
      element.setAttribute('role', 'alert');
      const copy = document.createElement('p');
      const retry = document.createElement('button');
      retry.type = 'button'; retry.textContent = 'Check cart again';
      retry.addEventListener('click', () => { void ensureReady().catch(showStatus); });
      element.append(copy, retry);
      const checkout = document.querySelector('[name="checkout"]');
      (checkout?.parentElement || document.querySelector('main') || document.body).append(element);
    }
    const copy = element.querySelector('p');
    const message = error?.message || 'We are still checking your cart update. Please check your cart again.';
    if (copy.textContent !== message) copy.textContent = message;
  };
  const refreshCart = (cart) => {
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: {
      resource: cart, sourceId: 'personalized-cart-recovery',
      data: { source: 'personalized-cart-recovery', skipAddTracking: true, itemCount: cart.item_count },
    } }));
  };
  const fingerprint = (cart) => JSON.stringify(cart.items.map((line) => [line.key, line.quantity, line.properties]));
  const persist = (value) => {
    savedOperation = value;
    try { if (value) sessionStorage.setItem(KEY, JSON.stringify(value)); else sessionStorage.removeItem(KEY); } catch (_) {}
  };
  const recalled = () => {
    if (savedOperation) return savedOperation;
    try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (_) { return null; }
  };
  const jsonRequest = async (path, body) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30_000);
    try {
      const response = await fetch(route(path), { credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        method: body ? 'POST' : 'GET', headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.errors || (typeof data.status === 'number' && data.status >= 400)) {
        const error = new Error(data.description || data.message || data.error || 'Your saved portraits could not be checked. Please try again.');
        error.status = response.status;
        throw error;
      }
      return { data, status: response.status };
    } finally { clearTimeout(timeout); }
  };
  const snapshot = async () => {
    const { data } = await jsonRequest('cart.js');
    if (!Array.isArray(data.items)) throw new Error('Your cart could not be checked. Please try again.');
    return data;
  };
  function wallets(locked) {
    for (const container of document.querySelectorAll('.additional-checkout-buttons')) {
      if (locked) {
        container.dataset.ppWalletWait = 'true';
        container.hidden = true;
        container.inert = true;
      } else if (container.dataset.ppWalletWait === 'true') {
        container.hidden = false;
        container.inert = false;
        delete container.dataset.ppWalletWait;
      }
    }
  }
  const prepare = async (line, countryCode) => {
    const requestId = crypto.randomUUID();
    const started = Date.now();
    const properties = line.properties;
    while (Date.now() - started < 120_000) {
      const result = await jsonRequest('/apps/pop-prints-backend/checkout-product', {
        sourceVariantId: properties['_PP Source Variant ID'], artworkUrl: properties['_Portrait URL'],
        designId: properties['_PP Design ID'], designToken: properties['_PP Design Token'], requestId,
        countryCode,
        ...(properties['_PP Presentation Version'] ? { presentationVersion: properties['_PP Presentation Version'] } : {}),
        ...(properties['_PP Portrait Shape'] ? { portraitShape: properties['_PP Portrait Shape'] } : {}),
        ...(properties['_PP Preview Theme ID'] ? { previewThemeId: properties['_PP Preview Theme ID'] } : {}),
      });
      if (result.status === 202 && result.data.status === 'pending') {
        await pause(Math.max(500, Math.min(5000, Number(result.data.retryAfterMs) || 1500)));
        continue;
      }
      const ready = result.data;
      if (ready.status !== 'ready' || !/^\d+$/.test(id(ready.variantId)) ||
          id(ready.sourceVariantId) !== id(properties['_PP Source Variant ID']) ||
          (ready.portraitShape || 'portrait') !== (properties['_PP Portrait Shape'] || 'portrait') ||
          (properties['_PP Preview Theme ID'] && String(ready.previewThemeId || '') !== String(properties['_PP Preview Theme ID'])) ||
          String(ready.designId) !== String(properties['_PP Design ID']) || !ready.properties || !ready.artworkUrl) {
        throw new Error('A saved portrait could not be verified. Please try again.');
      }
      return { ...ready, cartTrackingRequestId: requestId };
    }
    throw new Error('Your saved portrait is still preparing. Please try again in a moment.');
  };
  const matchNew = (cart, operation) => operation.items.every((expected) => cart.items.some((line) =>
    line.properties?.[MARKER] === operation.requestId && id(line.variant_id || line.id) === id(expected.id) &&
    line.properties?.['_PP Recovery Role'] === expected.properties['_PP Recovery Role'] &&
    Number(line.quantity) === Number(expected.quantity)));
  const oldUnchanged = (cart, operation) => operation.old.every((expected) => cart.items.some((line) =>
    line.key === expected.key && Number(line.quantity) === Number(expected.quantity) &&
    JSON.stringify(line.properties) === JSON.stringify(expected.properties)));
  async function finishReplacement(operation, cart) {
    if (!matchNew(cart, operation)) {
      const added = cart.items.some((line) => line.properties?.[MARKER] === operation.requestId);
      if (!added && operation.rejected) {
        // A definite rejection and a successful cart read prove this attempt added nothing.
        persist(null);
        return cart;
      }
      if (!added && !operation.old.some((old) => cart.items.some((line) => line.key === old.key))) {
        // The shopper removed every line involved in this repair. There is no old/new pair to reconcile.
        persist(null);
        return cart;
      }
      throw new Error('We could not confirm the cart update. Please review your cart before trying again; nothing will be added twice.');
    }
    window.PopPrintsPersonalizedCheckout?.confirmCartUse?.(operation.cartConfirmation);
    const remainingOld = operation.old.filter((expected) => cart.items.some((line) => line.key === expected.key));
    if (!remainingOld.length && operation.phase === 'removing') { persist(null); return cart; }
    if (!oldUnchanged(cart, operation)) {
      throw new Error('Your cart changed during the update. Please review the quantities before continuing.');
    }
    operation.phase = 'removing';
    persist(operation);
    const updates = Object.fromEntries(operation.old.map((line) => [line.key, 0]));
    // Exact old keys only. New lines, cart notes, attributes and discounts are preserved.
    try { await jsonRequest('cart/update.js', { updates }); } catch (_) { /* Verify even when the response is lost. */ }
    const checked = await snapshot();
    if (operation.old.some((old) => checked.items.some((line) => line.key === old.key)) || !matchNew(checked, operation)) {
      throw new Error('The cart update is still being confirmed. Please check your portraits again.');
    }
    persist(null);
    return checked;
  }
  async function replaceLine(cart, line, ready) {
    let pending;
    try { pending = JSON.parse(sessionStorage.getItem('popPrints.checkoutAttempt.v1') || 'null'); } catch (_) {}
    if (pending?.phase === 'submitted') {
      throw new Error('A previous addition still needs checking. Please review your cart before updating this portrait.');
    }
    if (fingerprint(await snapshot()) !== fingerprint(cart)) {
      throw new Error('Your cart changed. Please check your portraits again.');
    }
    const requestId = crypto.randomUUID();
    const main = { id: id(ready.variantId), quantity: line.quantity,
      properties: { ...line.properties, ...ready.properties, '_Portrait URL': ready.artworkUrl,
        '_PP Return URL': ready.returnUrl || '', [MARKER]: requestId, '_PP Recovery Role': 'portrait' } };
    const old = [line];
    const items = [main];
    const pair = line.properties['_Digital Download Key'];
    const addons = pair ? cart.items.filter((item) => item.key !== line.key &&
      item.properties?.['_Digital Download Key'] === pair &&
      id(item.properties?.['_Digital Download Parent Variant']) === id(line.variant_id || line.id)) : [];
    for (const [index, addon] of addons.entries()) {
      old.push(addon);
      items.push({ id: id(addon.variant_id || addon.id), quantity: addon.quantity,
        properties: { ...addon.properties, '_Digital Download Parent Variant': main.id,
          '_Digital Download Portrait URL': ready.artworkUrl, [MARKER]: requestId, '_PP Recovery Role': 'digital-' + index } });
    }
    const operation = { requestId, old, items, phase: 'adding', cartConfirmation: {
      requestId: ready.cartTrackingRequestId, productId: id(ready.productId), variantId: id(ready.variantId),
    } };
    persist(operation);
    try { await jsonRequest('cart/add.js', { items }); }
    catch (error) {
      // Timeouts and server failures can lose a committed response; do not replay them.
      if (error.status >= 400 && error.status < 500 && error.status !== 408) {
        operation.rejected = true;
        persist(operation);
      }
    }
    let checked = await snapshot();
    for (let retry = 0; retry < 2 && !matchNew(checked, operation); retry++) {
      await pause(1000);
      checked = await snapshot();
    }
    return finishReplacement(operation, checked);
  }
  async function recover(keys, countryCode) {
    clearStatus();
    wallets(true);
    let cart = await snapshot();
    const pending = recalled();
    if (pending) {
      cart = await finishReplacement(pending, cart);
      refreshCart(cart);
    }
    // Shopify already rendered availability. Only unavailable saved portraits
    // need recovery; recently added, available items need no further checks.
    const targets = cart.items.filter((line) => managed(line) && keys.includes(line.key));
    for (const original of targets) {
      const line = cart.items.find((item) => item.key === original.key);
      if (!line) continue;
      let ready;
      try { ready = await prepare(line, countryCode); }
      catch (_) { continue; } // Preserve the current line when optional preparation is unavailable.
      if (id(ready.variantId) !== id(line.variant_id || line.id) || ready.artworkUrl !== line.properties['_Portrait URL']) {
        cart = await replaceLine(cart, line, ready);
        refreshCart(cart);
      } else {
        window.PopPrintsPersonalizedCheckout?.confirmCartUse?.({
          requestId: ready.cartTrackingRequestId, productId: id(ready.productId), variantId: id(ready.variantId),
        });
      }
    }
    // Replacement writes already returned a verified cart. An unchanged cart
    // needs no final read before its ordinary Shopify checkout submission.
    wallets(false);
    return cart;
  }
  function ensureReady() {
    if (task) return task;
    const countryCode = country();
    const keys = recoveryKeys(countryCode);
    clearStatus();
    if (!recalled() && !keys.length) { wallets(false); return Promise.resolve(null); }
    for (const key of keys) attempted.add(countryCode + ':' + key);
    task = recover(keys, countryCode).catch((error) => {
      clearStatus();
      // An optional read/preparation failure must not block a normal cart.
      // An uncertain replacement still needs reconciliation before checkout:
      // allowing both old and new lines through could charge for duplicates.
      if (recalled()) { wallets(true); showStatus(error); throw error; }
      wallets(false);
      return null;
    })
      .finally(() => { task = null; });
    return task;
  }
  function schedule() {
    clearStatus();
    clearTimeout(scheduled);
    if (!task && !recalled() && !recoveryKeys().length) { wallets(false); return; }
    wallets(true);
    scheduled = setTimeout(() => {
      if (window.PopPrintsPersonalizedCheckout?.isBusy()) { schedule(); return; }
      void ensureReady().catch(() => {});
    }, 200);
  }
  const intercept = (event, button) => {
    if (bypass) return;
    // Preserve the native click/submit: no fetch, preparation or second click
    // unless an unavailable item or unfinished replacement actually needs work.
    if (!task && !recalled() && !recoveryKeys().length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    void ensureReady().then(() => {
      bypass = true;
      try {
        // Section rendering may have replaced the original checkout button.
        const current = document.querySelector('[name="checkout"]') || button;
        if (current?.isConnected) current.click();
        else location.assign(route('checkout'));
      } finally { bypass = false; checkoutPending = false; }
    }).catch((error) => {
      checkoutPending = false;
      showStatus(error);
      // Let a shopper in the drawer inspect the existing cart without adding
      // an error banner, retry controls, or a redirect loop on the cart page.
      if (location.pathname.replace(/\/$/, '') !== new URL(route('cart')).pathname.replace(/\/$/, '')) {
        location.assign(route('cart'));
      }
    });
  };
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[name="checkout"], a[href*="/checkout"]');
    if (button) intercept(event, button);
  }, true);
  document.addEventListener('submit', (event) => {
    if (event.submitter?.name === 'checkout') intercept(event, event.submitter);
  }, true);
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === 'personalized-cart-recovery') return;
    schedule();
  });
  const observer = new MutationObserver((records) => {
    const statusNode = (node) => node instanceof Element &&
      (node.matches('[data-pp-cart-recovery-status]') || node.closest('[data-pp-cart-recovery-status]'));
    if (records.every((record) => statusNode(record.target) ||
      [...record.addedNodes, ...record.removedNodes].every(statusNode))) return;
    clearStatus();
    if (!task) schedule();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  window.PopPrintsPersonalizedCart = { ensureReady, waitForIdle: () => task || Promise.resolve() };
  schedule();
})();
